import {
  Button,
  Group,
  Lightbox,
  Skeleton,
  Stack,
  Text,
  Upload,
} from "@assetwatch/ui";
import { type SyntheticEvent, useRef, useState } from "react";
import type { WorkOrderId } from "../../../lib/workIds";
import {
  useWorkOrderCommentsMutations,
  useWorkOrderFilesQuery,
  workOrderFilesStaleTime,
} from "../../../queries/useWorkOrderComments";
import {
  attachmentAccept,
  type WorkOrderAttachmentUpload,
} from "./useWorkOrderAttachmentUpload";
import { WorkOrderAttachmentTile } from "./WorkOrderAttachmentTile";

/** Matches the server's limit on active files attached to one Work Order. */
export const maxWorkOrderAttachmentCount = 20;

function fileCountLabel(count: number) {
  return `${count} ${count === 1 ? "file" : "files"}`;
}

/**
 * The Work Order's directly attached files, shown as the last section of the
 * Work Info card (the POC's `WoDetails` Attachments block). Comment
 * attachments are not listed here.
 */
export function WorkOrderAttachmentsSection({
  workOrderId,
  isCompleted,
  isCanceled,
  upload,
}: {
  workOrderId: WorkOrderId;
  isCompleted: boolean;
  isCanceled: boolean;
  upload: WorkOrderAttachmentUpload;
}) {
  const query = useWorkOrderFilesQuery(workOrderId);
  const mutations = useWorkOrderCommentsMutations(workOrderId);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const refreshedForUpdate = useRef<number | null>(null);
  const tileButtons = useRef(new Map<string, HTMLButtonElement>());
  const files = query.data?.files ?? [];
  const canChange = !isCompleted && !isCanceled;
  const atLimit = files.length >= maxWorkOrderAttachmentCount;

  // Download URLs expire; a failed image load most likely means an expired
  // URL, so refetch once per loaded list rather than on every failure.
  function refreshExpiredUrls() {
    if (refreshedForUpdate.current === query.dataUpdatedAt) return;
    refreshedForUpdate.current = query.dataUpdatedAt;
    void query.refetch();
  }

  function openLightbox(index: number) {
    if (Date.now() - query.dataUpdatedAt > workOrderFilesStaleTime)
      void query.refetch();
    setLightboxIndex(index);
    setLightboxOpen(true);
  }

  function changeLightboxOpen(open: boolean) {
    setLightboxOpen(open);
    if (open) return;
    const fileId = files[lightboxIndex]?.id;
    const tile = fileId ? tileButtons.current.get(fileId) : undefined;
    // Return focus to the tile for the image last shown in the viewer.
    if (tile) requestAnimationFrame(() => tile.focus());
  }

  function handleLightboxImageError(event: SyntheticEvent) {
    if (
      event.target instanceof Element &&
      event.target.closest('[data-slot="lightbox"]')
    )
      refreshExpiredUrls();
  }

  return (
    <div onError={handleLightboxImageError}>
      <Stack gap={12}>
        <Group justify="between">
          <Text size="sm" tone="subtle">
            Attachments
          </Text>
          {query.data ? (
            <Text size="sm" tone="subtle">
              {fileCountLabel(files.length)}
            </Text>
          ) : null}
        </Group>
        {query.isPending ? (
          <Skeleton height="var(--spacing-128)" width="100%" />
        ) : query.isError ? (
          <Stack align="start" gap={8}>
            <Text size="sm" tone="negative">
              Unable to load attachments.
            </Text>
            <Button
              onClick={() => void query.refetch()}
              size="sm"
              variant="outline"
            >
              Retry
            </Button>
          </Stack>
        ) : files.length === 0 ? (
          <Text size="sm" tone="subtle">
            No files attached.
          </Text>
        ) : (
          <div className="grid grid-cols-2 gap-(--spacing-12) sm:grid-cols-4">
            {files.map((file, index) => (
              <WorkOrderAttachmentTile
                canRemove={canChange && file.canRemove}
                file={file}
                key={file.id}
                onImageError={refreshExpiredUrls}
                onOpen={() => openLightbox(index)}
                onRemove={() =>
                  mutations.removeFile.mutate({
                    fileId: file.id,
                    commentId: null,
                  })
                }
                openButtonRef={(element) => {
                  if (element) tileButtons.current.set(file.id, element);
                  else tileButtons.current.delete(file.id);
                }}
                removePending={mutations.removeFile.isPending}
              />
            ))}
          </div>
        )}
        {canChange ? (
          <>
            <Upload
              accept={attachmentAccept}
              acceptedFormatsLabel="JPEG, PNG, WebP, HEIC, HEIF, TIFF"
              buttonLabel="Attach file to Work Order"
              disabled={
                upload.isUploading ||
                upload.convertingFileName !== null ||
                atLimit ||
                upload.directUpload !== null
              }
              maxSizeLabel="25 MB; HEIC and TIFF sources up to 100 MB"
              onFilesSelected={(selected) => {
                const file = selected[0];
                if (file) upload.start(file);
              }}
              variant="photo"
            />
            {atLimit ? (
              <Text size="sm" tone="subtle">
                {`A Work Order can have at most ${maxWorkOrderAttachmentCount} active attachments.`}
              </Text>
            ) : null}
            {upload.convertingFileName ? (
              <Group justify="between">
                <Text size="sm" tone="subtle">
                  {upload.convertingFileName}
                </Text>
                <Text role="status" size="sm" tone="subtle">
                  Converting…
                </Text>
              </Group>
            ) : null}
            {upload.directUpload ? (
              <Group justify="between">
                <Text size="sm" tone="subtle">
                  {upload.directUpload.file.name}
                </Text>
                <Group gap={8}>
                  <Button
                    disabled={upload.isUploading}
                    onClick={upload.retry}
                    size="sm"
                    variant="ghost"
                  >
                    Retry attachment
                  </Button>
                  <Button
                    disabled={upload.isUploading}
                    onClick={upload.discard}
                    size="sm"
                    variant="ghost"
                  >
                    Discard
                  </Button>
                </Group>
              </Group>
            ) : null}
            {upload.error ? (
              <Text size="sm" tone="negative">
                {upload.error}
              </Text>
            ) : null}
          </>
        ) : null}
      </Stack>
      <Lightbox
        aria-label="Work Order attachments"
        images={files.map((file) => ({
          src: file.downloadUrl,
          alt: file.fileName,
        }))}
        index={Math.min(lightboxIndex, Math.max(files.length - 1, 0))}
        onIndexChange={setLightboxIndex}
        onOpenChange={changeLightboxOpen}
        open={lightboxOpen && files.length > 0}
      />
    </div>
  );
}
